import { useEffect, useState } from 'react';
import { makeStyles, tokens, Title1, Text, Spinner } from '@fluentui/react-components';
import {
  getBusinessImpactSummary,
  type BusinessImpactSummary,
} from '../api/businessImpact';

// Workbench MVP Session B — Business Impact projection.
// Observed spend across chat + benchmark + matrix, projected to a monthly
// bill using two config knobs (echoed in the DTO so the assumptions surface
// on the tile itself).

const useStyles = makeStyles({
  header: { marginBottom: '18px' },
  kpiGrid: {
    display: 'grid',
    gap: '14px',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    marginBottom: '18px',
  },
  kpi: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '14px 16px',
  },
  kpiLabel: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.04em',
  },
  kpiValue: {
    fontSize: '26px',
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums' as const,
    marginTop: '4px',
  },
  kpiSubtle: {
    color: tokens.colorNeutralForeground3,
    fontSize: '12px',
    marginTop: '4px',
  },
  table: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '8px',
    padding: '10px 14px',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1.2fr 0.8fr 0.8fr 0.8fr',
    gap: '10px',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    fontSize: '13px',
    fontVariantNumeric: 'tabular-nums' as const,
  },
  rowHead: {
    color: tokens.colorNeutralForeground3,
    fontWeight: 600,
    fontSize: '12px',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.03em',
  },
});

function fmtUsd(v: number, maxFrac = 4): string {
  return v.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: maxFrac,
  });
}

export default function BusinessImpact() {
  const s = useStyles();
  const [summary, setSummary] = useState<BusinessImpactSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBusinessImpactSummary(7)
      .then(setSummary)
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="business-impact-page">
      <div className={s.header}>
        <Title1 as="h1">Business Impact</Title1>
        <Text as="p" size={300}>
          Observed spend across every run type projected onto a monthly bill.
          Assumptions surface on the tile so the audience sees the levers rather
          than trusting a hardcoded number.
        </Text>
      </div>

      {loading && <Spinner label="Loading impact projection…" />}
      {err && (
        <Text as="p" style={{ color: tokens.colorPaletteRedForeground1 }}>
          {err}
        </Text>
      )}

      {summary && !loading && (
        <>
          <div className={s.kpiGrid}>
            <div className={s.kpi}>
              <div className={s.kpiLabel}>Observed spend ({summary.windowDays}d)</div>
              <div className={s.kpiValue} data-testid="observed-total">
                {fmtUsd(summary.observedTotalCostUsd)}
              </div>
              <div className={s.kpiSubtle}>
                {summary.observedSessionCount} session(s)
              </div>
            </div>
            <div className={s.kpi}>
              <div className={s.kpiLabel}>Avg $ / session</div>
              <div className={s.kpiValue} data-testid="avg-per-session">
                {fmtUsd(summary.avgCostPerSessionUsd)}
              </div>
            </div>
            <div className={s.kpi}>
              <div className={s.kpiLabel}>Projected monthly</div>
              <div className={s.kpiValue} data-testid="projected-monthly">
                {fmtUsd(summary.projectedMonthlyCostUsd, 2)}
              </div>
              <div className={s.kpiSubtle}>
                {summary.assumedUsersPerMonth.toLocaleString()} users ×{' '}
                {summary.assumedSessionsPerUser} sessions/user
              </div>
            </div>
            <div className={s.kpi}>
              <div className={s.kpiLabel}>Projected $ / user / month</div>
              <div className={s.kpiValue} data-testid="projected-per-user">
                {fmtUsd(summary.projectedCostPerUserPerMonthUsd, 2)}
              </div>
            </div>
          </div>

          <div className={s.table} data-testid="by-source">
            <div className={`${s.row} ${s.rowHead}`}>
              <span>Source</span>
              <span>Total</span>
              <span>Sessions</span>
              <span>Avg / session</span>
            </div>
            {summary.bySource.map((row) => (
              <div key={row.source} className={s.row} data-testid={`source-${row.source}`}>
                <span>{row.source}</span>
                <span>{fmtUsd(row.totalCostUsd)}</span>
                <span>{row.sessionCount}</span>
                <span>{fmtUsd(row.avgCostPerSessionUsd)}</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
